import React from 'react';
import {apiGetDepots} from 'constants/Api';
import {connect} from 'react-redux';
import {IDepot, ISupplierUserProfile} from 'constants/interfaces';
import AddIcon from '@material-ui/icons/Add';
import {Box, Button, Snackbar, Typography} from '@material-ui/core';
import {ESidePanelView, ESupplierUserPermissions} from 'constants/index';
import {reduxToggleSidePanel, reduxSetDepots} from 'appredux/actions';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {NavigationService} from 'services/index';
import {Row, KhulaSupplierTable} from 'components/Table/KhulaSupplierTable';
import {
  depotTableFilters,
  depotTableHeaders,
  depotTableRows,
} from 'screens/Dashboard/Depots/DepotTableSetup';
import moment from 'moment';
import {DepotState} from 'appredux/reducers/reducer-depots';
import {PermissionsService} from 'services/PermissionsService';

type DepotProps = {
  user: ISupplierUserProfile;
  hideTitle?: boolean;
  depotState: DepotState;
};

export class Depots extends React.Component<DepotProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    depotToRemove?: IDepot;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    depotToRemove: undefined,
  };

  componentDidMount() {
    const {depotState} = this.props;
    const noDepots = !depotState.depots?.length;
    this.setState({isLoading: noDepots}, () => {
      this.loadDepots()
        .catch((err) => {
          alert('Error loading depots. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false}));
    });
  }

  loadDepots(): Promise<void> {
    const {user, depotState} = this.props;
    const {lastUpdate, depots} = depotState;

    const depotsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noDepots = !depots?.length;

    if (depotsAreExpired || noDepots)
      return apiGetDepots(user)
        .then((depots) => reduxSetDepots(depots))
        .then(() => {});

    return Promise.resolve();
  }

  refreshDepots() {
    const {user} = this.props;
    this.setState({isRefreshing: true}, () => {
      apiGetDepots(user)
        .then((depots: IDepot[]) => {
          reduxSetDepots(depots);
          this.setState({showRefreshedSnackbar: true});
        })
        .catch((err) => {
          alert('Error refreshing depots. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isRefreshing: false}));
    });
  }

  addDepot() {
    const type = {
      panelType: ESidePanelView.ADD_DEPOT,
      data: {},
    };
    reduxToggleSidePanel(true, type, false);
  }

  editDepot(depot: IDepot) {
    const type = {
      panelType: ESidePanelView.EDIT_DEPOT,
      data: depot,
    };
    reduxToggleSidePanel(true, type, false);
  }

  removeDepot(depot: IDepot) {
    this.setState({depotToRemove: depot});
  }

  confirmRemoveDepot(depot: IDepot) {
    new Promise((resolve) => setTimeout(resolve, 1000))
      .then(() => console.log('mock removed depot', depot))
      .then(() => {
        NavigationService.navigate('/');
        setTimeout(() => NavigationService.navigate('depots'), 50);
      })
      .catch((err) => {
        alert('Unable to remove depot at the moment. Please try again later.');
        console.error(err);
      })
      .finally(() => this.setState({depotToRemove: undefined}));
  }

  render() {
    const {depotState} = this.props;
    const {lastUpdate, depots} = depotState;
    const {isLoading, isRefreshing, showRefreshedSnackbar, depotToRemove} =
      this.state;

    const mapRowToDepot = (row: Row) =>
      // @ts-ignore
      depots.find((depot) => depot.id == row.id);

    const permissions = new PermissionsService(this.props.user);
    const canWrite = permissions.hasPermission(
      ESupplierUserPermissions.DEPOT_WRITE,
    );

    return (
      <div className="table-screen depots-container">
        {!this.props.hideTitle && (
          <Typography variant="h5" className="page-title">
            Depots
          </Typography>
        )}
        <Box className="actions-row">
          {canWrite && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => this.addDepot()}
              size="small"
              color={'primary'}
            >
              Add Depot
            </Button>
          )}
        </Box>
        <div className="depots-section">
          <KhulaSupplierTable
            headers={depotTableHeaders}
            rows={depotTableRows(depots, (depot) => this.editDepot(depot))}
            defaultSortField={'id'}
            filtersGroups={depotTableFilters}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={() => this.refreshDepots()}
            onRowSelect={(row: any) => {
              const depot = mapRowToDepot(row);
              if (!depot) return;
              this.editDepot(depot);
            }}
          />
          <Box mt={1}>
            <Typography variant={'caption'}>
              Last updated: {moment(lastUpdate).fromNow()}
            </Typography>
          </Box>
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showRefreshedSnackbar}
            onClose={() => this.setState({showRefreshedSnackbar: false})}
            autoHideDuration={3000}
            message="Depots refreshed"
          />
          <RemoveDepotDialog
            open={!!depotToRemove}
            depot={depotToRemove}
            onConfirm={() => this.confirmRemoveDepot(depotToRemove!)}
            onCancel={() => this.setState({depotToRemove: undefined})}
          />
        </div>
      </div>
    );
  }
}

function RemoveDepotDialog(props: {
  open: boolean;
  depot?: IDepot;
  onConfirm: () => any;
  onCancel: () => any;
}) {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>Confirm Remove Depot</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to remove {props.depot?.name} depot? You will
          not be able to undo.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button onClick={props.onConfirm} className="warning-button" autoFocus>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(Depots);
